import * as React from "react";

import {
  DATAKTP_TARGET,
  DATAKTP_EXECUTE_TARGET,
  DATAKTP_ARIA_TARGET,
} from "@fluentui/react";
import {
  getNativeElementProps,
  slot,
  useSplitButton_unstable,
} from "@fluentui/react-components";
import type { SplitButtonProps } from "@fluentui/react-components";

import { dataUniqueId } from "@1js/acui-common";
import { useMergedTooltipAndKeytipRef } from "@1js/acui-hooks";
import { AppContextualMenu } from "@1js/acui-menu";

import { deriveButtonKeytip } from "../../../utilities";
import { useShadowRenderedMenu } from "../../AppFlyoutAnchor/next/useShadowRenderedMenu";
import { AppSplitButtonPrimaryButton } from "./AppSplitButtonPrimaryButton";
import { AppSplitButtonMenuButton } from "./AppSplitButtonMenuButton";
import { useAppSplitButtonState } from "./useAppSplitButtonState";
import type { AppButtonProps } from "../../AppButton/next";
import type { AppMenuButtonProps } from "../../AppFlyoutAnchor/next";
import type {
  AppSplitButtonProps,
  AppSplitButtonState,
} from "./AppSplitButton.types";

export const useAppSplitButton = (
  props: AppSplitButtonProps,
  ref: React.Ref<HTMLButtonElement>
): AppSplitButtonState => {
  const {
    menuDefinition,
    deferredMenuDefinition,
    shadowRenderMenu,
    onMenuButtonExecute,
    location,
    ariaRoleDescription,
    dataAutomationType,
    disabled,
    primaryDisabled,
    toggleable,
    toggled,
    fullDescriptionLabel,
    splitButtonAriaLabel,
    id,
  } = props;

  // We want to trigger the menuButton part of the splitButton on keytips
  // Create a ref and assign it down below
  const menuButtonRef = React.useRef<HTMLButtonElement>();
  const mergedRef = useMergedTooltipAndKeytipRef(
    {
      ...props,
      deriveKeytip: deriveButtonKeytip,
      directClickRef: menuButtonRef,
      useDirectClick: true,
    },
    ref,
    "AppButton"
  );

  const splitButtonState = useSplitButton_unstable(
    props as SplitButtonProps,
    mergedRef
  );

  const primaryActionButtonDefaultProps = {
    ...props,
    // Take primaryDisabled into account for primary button
    disabled: disabled || primaryDisabled,
    root: {
      "aria-roledescription": ariaRoleDescription,
    },
  } as AppButtonProps;

  const menuButtonDefaultProps = {
    ...props,
    // Correctly assign menu button-specific props
    onExecute: onMenuButtonExecute,
    // Combine primary button aria-label and split button aria-label
    ariaLabel: [fullDescriptionLabel, splitButtonAriaLabel].join(" "),
    // Unset icon so that it only shows the chevron
    icon: undefined,
    // Unset aria description properties as these only go on the primary button
    ariaDescription: undefined,
    customTooltip: undefined,
    "data-ktp-execute-target": DATAKTP_EXECUTE_TARGET,
    "data-ktp-aria-target": DATAKTP_ARIA_TARGET,
    ref: menuButtonRef,
  } as AppMenuButtonProps;

  const rootProps = {
    ...props,
    "data-unique-id": dataUniqueId({ id, location }),
    "aria-roledescription": ariaRoleDescription,
    "data-automation-type": dataAutomationType,
    "data-ktp-target": DATAKTP_TARGET,
    role: "group",
  };

  const shadowRenderedMenuState = useShadowRenderedMenu(
    // For menu functionality, "onMenuButtonExecute" should be treated as "onExecute"
    { ...props, onExecute: props.onMenuButtonExecute },
    "SplitButton"
  );
  const defaultMenuProps = shadowRenderedMenuState.getMenuProps(menuDefinition);

  const state: AppSplitButtonState = {
    ...splitButtonState,
    toggleable,
    toggled,

    shadowRenderMenu,
    ...shadowRenderedMenuState,

    // Slots definition
    components: {
      root: "div",
      primaryActionButton: AppSplitButtonPrimaryButton,
      menuButton: AppSplitButtonMenuButton,
      menu: deferredMenuDefinition?.menuAs ?? AppContextualMenu,
    },

    root: slot.always(
      getNativeElementProps("div", { ref: mergedRef, ...rootProps }),
      { elementType: "div" }
    ),
    primaryActionButton: slot.always(props.primaryActionButton, {
      defaultProps: primaryActionButtonDefaultProps,
      elementType: AppSplitButtonPrimaryButton,
    }),
    menuButton: slot.always(props.menuButton, {
      defaultProps: menuButtonDefaultProps,
      elementType: AppSplitButtonMenuButton,
    }),
    menu: slot.always(props.menu, {
      defaultProps: defaultMenuProps,
      elementType: deferredMenuDefinition?.menuAs ?? AppContextualMenu,
    }),
  };

  return useAppSplitButtonState(props, state);
};
